import { RoundedBadge } from '@/components/rounded-badge';
import { RafflePrize } from '@/hooks/useRaffle';
import { formatCurrencyToLocale, formatNumberToLocale, getImgPathFromPrizeId } from '@/utils';
import { Box, Card, Heading, HStack, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { RafflePrizeCardSkeleton } from '../Skeletons';
import { Winners } from './Winners';

export const PreviousWinnersList = ({
  rafflePrizes,
  isLoading,
  isLoadingDailySpinOffchain,
  placeholder,
  isAuthenticated,
}: {
  rafflePrizes: RafflePrize[];
  isLoading: boolean;
  numTicketsToSubmit: number;
  setNumTicketsToSubmit: (n: number) => void;
  raffleTicketsBalance: number;
  placeholder: string;
  onRefresh: () => void;
  isAuthenticated: boolean;
  isLoadingDailySpinOffchain: boolean;
}) => {
  if (isLoading) return <RafflePrizeCardSkeleton />;
  if (rafflePrizes.length)
    return (
      <>
        {rafflePrizes.map((rafflePrize) => (
          <RafflePrizeCard
            key={rafflePrize.id}
            rafflePrize={rafflePrize}
            isAuthenticated={isAuthenticated}
            isLoadingUserTickets={isLoadingDailySpinOffchain}
          />
        ))}
      </>
    );
  return placeholder;
};

export const RafflePrizeCard = ({
  rafflePrize,
  isAuthenticated,
  isLoadingUserTickets,
}: {
  rafflePrize: RafflePrize;
  isAuthenticated: boolean;
  isLoadingUserTickets: boolean;
}) => {
  const { name, value, totalTickets: tickets, yourTickets, endsAt, winners, id: prizeId } = rafflePrize;
  const ended = endsAt ? endsAt.getTime() < Date.now() : false;
  return (
    <>
      <Card.Root w="full" borderRadius="8px" overflow="hidden" hideFrom="md">
        <Card.Body p={6}>
          <VStack alignItems="start" gap={4}>
            <HStack gap={4}>
              <Box
                w="90px"
                rounded="16px"
                h="90px"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                bg="white"
                flexShrink={0}
              >
                <Image src={getImgPathFromPrizeId(prizeId, 'disabled')} w="100%" h="100%" objectFit="contain" />
              </Box>
              <Heading fontSize="lg">{name}</Heading>
            </HStack>
            <VStack alignItems="start" gap={3} w="full">
              <RaffleStatSmall title="Value" value={formatCurrencyToLocale(value).replace('.00', '')} />

              <RaffleStatSmall title="Tickets" value={formatNumberToLocale(tickets)} />

              {isAuthenticated && (
                <RaffleStatSmall title="Your Tickets" value={formatNumberToLocale(yourTickets || 0)} />
              )}

              {ended && winners && winners.length > 0 && (
                <RaffleStatSmall title="Winners" value={<Winners rafflePrize={rafflePrize} />} />
              )}
            </VStack>
          </VStack>
        </Card.Body>
      </Card.Root>
      <Card.Root w="full" borderRadius="2xl" overflow="hidden" hideBelow="md">
        <HStack h="full" gap={0}>
          <Box
            w="174px"
            h="174px"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            bg="white"
            flexShrink={0}
          >
            <Image src={getImgPathFromPrizeId(prizeId, 'disabled')} w="100%" h="100%" objectFit="contain" />
          </Box>
          <VStack alignSelf="stretch" w="full">
            <HStack
              alignSelf="stretch"
              justifyContent="space-between"
              bg="bg"
              flex="1"
              padding={6}
              borderBottom="1px solid #F0F0F0"
            >
              <Box>
                <Heading size="md">{name}</Heading>
              </Box>
            </HStack>
            <HStack alignSelf="stretch" justifyContent="space-between" flex="1" padding={6}>
              <HStack flex="1" justifyContent="start">
                <RaffleStat title="Value" value={formatCurrencyToLocale(value).replace('.00', '')} />
                <RaffleStat title="Tickets" value={formatNumberToLocale(tickets)} />
                {isAuthenticated && (
                  <RaffleStat
                    title="Your Tickets"
                    value={formatNumberToLocale(yourTickets || 0)}
                    loading={isLoadingUserTickets}
                  />
                )}
                {ended && winners && winners.length > 0 && (
                  <RaffleStat title="Winners" value={<Winners rafflePrize={rafflePrize} />} />
                )}
              </HStack>
              {endsAt && (
                <RoundedBadge h={9} bgColor={ended ? 'bg.muted' : '#FFF6C7'} color={ended ? 'fg.muted' : '#963D01'}>
                  {ended ? `Picked on ${endsAt.toLocaleDateString()}` : 'Ending Soon!'}
                </RoundedBadge>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Card.Root>
    </>
  );
};

const RaffleStatSmall = ({ title, value }: { title: string; value: number | string | ReactElement }) => (
  <HStack justifyContent="space-between" w="full">
    <Text fontWeight="500" color="text.secondary">
      {title}
    </Text>
    <Heading fontSize="lg" fontWeight="500">
      {value}
    </Heading>
  </HStack>
);

const RaffleStat = ({
  title,
  value,
  loading = false,
}: {
  title: string;
  value: number | string | ReactElement;
  loading?: boolean;
}) => (
  <Box maxW={150} flex="1">
    <Text color="fg.muted" fontWeight="500" textWrap="nowrap">
      {title}
    </Text>
    {loading ? (
      <Spinner />
    ) : (
      <Heading size="md" fontWeight="500">
        {value}
      </Heading>
    )}
  </Box>
);
